import Container from '@/components/Container';

import LessonCard from '@/components/Learn/Lesson';
import DefaultContentTemplateAuthorDetailed from '@/components/DefaultContentTemplate/AuthorDetailed';
import DownloadableContent from '@/components/DefaultContentTemplate/DownloadableContent';
import DefaultContentTemplateFaq from '@/components/DefaultContentTemplate/Faq';
import DefaultContentTemplateEmbedded from '@/components/DefaultContentTemplate/Embedded';
import { Enum_Course_Type, Enum_Like_Type, GENERATED } from '@edwin-group/cj-graphql';
import type {
  AuthorRelationResponseCollection,
  ComponentCommonAuthorDetailed,
  ComponentCommonDownloadableContentList,
  ComponentCommonFaqComponent,
  ComponentCommonMedia,
  ComponentLearnCourseLesson,
  Course,
  Learn,
  LearnItem,
  LearnPage,
} from '@edwin-group/cj-graphql/generated/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCourseLessonBySlug, fetchRelatedContent, normalizeLearnItems } from '@/services/learn/course.service';
import { Link, useParams, useNavigate } from 'react-router-dom';
import DefaultContentTemplateAudio from '@/components/DefaultContentTemplate/Audio';
import DefaultContentTemplateMedia from '@/components/DefaultContentTemplate/Media';
import CourseSkeleton from '../course/Course.skeleton';
import { completeLearn, completeLesson, createLearn, fetchCourseLearn, getPage } from '@/services/learn/learn.service';
import classNames from 'classnames';
import CheckIcon from '@/components/Icons/CheckIcon';
import DefaultContentTemplateTextEditor from '@/components/DefaultContentTemplate/TextEditor';
import Button from '@/components/Button';
import { ChevronDoubleLeftIcon } from '@heroicons/react/24/solid';
import { getCurrentLearn } from '@/utils/learn';
import Navigation from '@/components/ui/navigation';
import { navigationCourse } from '../constants';
import Breadcrumb from '@/components/ui/breadcrumb';
import Divider from '@/components/Divider';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import TitleWithActions from '@/components/TitleWithActions';
import DialogConfirmMarkCourseAsCompleted from '../../../components/confirmDialog';
import { ComponentCommonTextEditorWithHtml } from '@/components/DefaultContentTemplate';
import CourseCard from '@/components/Card/Course';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';

export type PendingLesson = ComponentLearnCourseLesson & {
  idModule: string;
};

function CourseLessonPage() {
  const navigate = useNavigate();
  const [cx, setCourse] = useState<Course | null>(null);
  const [normalizedLearnItems, setNormalizedLearnItems] = useState<LearnItem[] | null>([]);
  const [relatedContent, setRelatedContent] = useState<Array<Course> | null>(null);
  const [learn, setLearn] = useState<Learn | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [pendingLessons, setPendingLessons] = useState<PendingLesson[]>([]);
  const [learnPage, setLearnPage] = useState<LearnPage | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingDoneLesson, setLoadingDoneLesson] = useState<boolean>(false);
  const { slug, idModule, idLesson } = useParams();

  const c = cx?.attributes;

  const { currentModule, currentLesson, nextLesson, idNextLessonModule, previousIdLesson, previousLessonidModule } =
    getCurrentLearn(c, idModule, idLesson);

  const pendingLessonsFromNormalized = useMemo(() => {
    return cx?.attributes?.modules?.reduce((acc, currModule) => {
      const pendingLessons = (currModule?.lessons || [])
        .filter((lesson) => {
          const isDone = normalizedLearnItems?.some(
            (learnItem) =>
              learnItem?.idModule === currModule?.moduleStableId && learnItem?.idLesson === lesson?.lessonStableId,
          );
          return !isDone;
        })
        .map((lesson) => ({ ...lesson, idModule: currModule?.moduleStableId }));

      return acc.concat(pendingLessons);
    }, [] as unknown[]);
  }, [cx?.attributes?.modules, normalizedLearnItems]);

  const isDoneCurrentLesson = useMemo(
    () =>
      !!normalizedLearnItems?.find((learnItem) => {
        return (
          learnItem?.idModule === currentModule?.moduleStableId && learnItem?.idLesson === currentLesson?.lessonStableId
        );
      }),
    [currentLesson?.lessonStableId, currentModule?.moduleStableId, normalizedLearnItems],
  );

  const isLastLesson = useMemo(() => {
    return !nextLesson;
  }, [nextLesson]);

  const lessonsCount = useMemo(
    () =>
      cx?.attributes?.modules?.reduce((acc, curr) => {
        return acc + (curr?.lessons || [])?.length || 0;
      }, 0),
    [cx?.attributes?.modules],
  );

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);

      const [courseResult, relatedContentResult, learnResult, pageResponse] = await Promise.all([
        fetchCourseLessonBySlug(slug),
        fetchRelatedContent(slug),
        fetchCourseLearn(slug),
        getPage(),
      ]);

      setLearnPage(pageResponse.page);
      setCourse(courseResult);
      setRelatedContent(relatedContentResult || []);
      setLearn(learnResult as unknown as Learn);
      setNormalizedLearnItems(normalizeLearnItems(learnResult as unknown as Learn) as LearnItem[]);

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const createCurrentLearn = useCallback(async () => {
    if (!slug || !cx?.id || learn?.id) {
      return;
    }

    const learnResult = await createLearn(cx.id);
    setLearn(learnResult?.data?.createLearn?.data as unknown as Learn);
  }, [cx?.id, slug, learn?.id]);

  const markCourseAsCompleted = useCallback(
    async (skipCheckLesson?: boolean) => {
      if (!slug || !cx?.id || !learn?.id) {
        return;
      }

      if (pendingLessonsFromNormalized && pendingLessonsFromNormalized.length > 0 && !skipCheckLesson) {
        setIsOpenDialog(true);
        setPendingLessons(pendingLessonsFromNormalized as PendingLesson[]);
        return;
      }

      if (!learn?.attributes?.completed && c?.type !== Enum_Course_Type.Training) {
        navigate(`/learn/course/${c?.slug}/feedback`);
      }

      if (!learn?.attributes?.completed) {
        trackGAEvent('course_completed', {
          course_document_id: cx?.documentId ?? '',
          course_name: cx?.title ?? cx?.attributes?.title ?? '',
        });
        await completeLearn(learn!.documentId!);
      }
      navigate(`/learn/course/${c?.slug}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cx?.id, slug, learn?.id, learn?.attributes?.completed, lessonsCount, normalizedLearnItems],
  );

  const completeCurrentLesson = useCallback(async () => {
    if (!currentModule?.moduleStableId || !currentLesson?.lessonStableId || !learn?.id || isDoneCurrentLesson) {
      return null;
    }
    setLoadingDoneLesson(true);

    try {
      const learnResult = await completeLesson(currentLesson.lessonStableId, currentModule.moduleStableId, learn.id);

      const studiedItems = normalizeLearnItems(
        learnResult?.data?.createLearnItem?.data?.attributes?.learn?.data as unknown as Learn,
      ) as LearnItem[];

      if (
        !learn?.attributes?.completed &&
        studiedItems?.length === lessonsCount &&
        c?.type !== Enum_Course_Type.Training
      ) {
        navigate(`/learn/course/${c?.slug}/feedback`);
      }

      setNormalizedLearnItems(studiedItems);
    } finally {
      setLoadingDoneLesson(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentLesson?.lessonStableId,
    currentModule?.moduleStableId,
    isDoneCurrentLesson,
    learn?.attributes?.completed,
    learn?.id,
    lessonsCount,
  ]);

  useEffect(() => {
    if (!cx?.id || !idLesson || !idModule) {
      return;
    }

    createCurrentLearn();
  }, [learn?.id, cx?.id, idLesson, idModule, createCurrentLearn]);

  if (!c || !idModule || !idLesson) {
    return null;
  }

  if (loading) {
    return <CourseSkeleton />;
  }

  const items = [
    {
      label: <>Learn</>,
      url: '/learn',
    },
    {
      label: <>Courses</>,
      url: '/learn',
    },
    { label: c?.title, url: `/learn/course/${c?.slug}` },
    { label: currentLesson?.title },
  ];

  const renderMarkAsDoneButton = () => {
    const isTraining = c?.type === Enum_Course_Type.Training;
    return (
      <>
        {isLastLesson &&
          isDoneCurrentLesson &&
          (!pendingLessonsFromNormalized || pendingLessonsFromNormalized.length === 0) &&
          !isTraining && (
            <Button
              onClick={() => {
                if (c?.type === Enum_Course_Type.Training) {
                  return;
                }
                navigate(`/learn/course/${c?.slug}/feedback`);
              }}>
              {learnPage?.markLessonAsCompleteButton?.markAsCompleteLabel || 'MARK AS COMPLETE'}
            </Button>
          )}
        {(!isLastLesson || !isDoneCurrentLesson) && (
          <Button
            onClick={completeCurrentLesson}
            theme={isDoneCurrentLesson ? 'outline' : 'solid'}
            loading={loadingDoneLesson}
            className="flex items-center justify-center text-sh-xxs"
            disabled={isDoneCurrentLesson || loadingDoneLesson}>
            {!isDoneCurrentLesson && (learnPage?.markLessonAsCompleteButton?.markAsCompleteLabel || 'MARK AS COMPLETE')}
            {isDoneCurrentLesson && (learnPage?.markLessonAsCompleteButton?.completedLabel || 'COMPLETED')}
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <DialogConfirmMarkCourseAsCompleted
        learnPage={learnPage}
        setOpen={setIsOpenDialog}
        open={isOpenDialog}
        lessons={pendingLessons}
        course={c}
      />
      <Navigation title="Learn" items={navigationCourse} />
      <AnimatedFadeInComponent>
        <Breadcrumb items={items} />
      </AnimatedFadeInComponent>
      <Container className="default-content-template min-h-40">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <div className="grid grid-cols-subgrid gap-sh-30">
            <div className="flex flex-col gap-sh-30">
              <AnimatedFadeInComponent>
                <p className="font-cadiz text-sh-28-32 font-bold">{c?.title}</p>
              </AnimatedFadeInComponent>
              {isDoneCurrentLesson ? (
                <AnimatedFadeInComponent>
                  <div className="flex justify-center rounded-2xl border border-sh-blue-light bg-boxes-background p-5">
                    <h4 className="flex items-center font-cadiz text-sh-28-32 font-bold">
                      {'You completed this lesson'}
                    </h4>
                  </div>
                </AnimatedFadeInComponent>
              ) : null}

              {currentLesson?.highlightContent?.embedded ? (
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateEmbedded
                    className="!my-0"
                    fitContent
                    component={currentLesson?.highlightContent?.embedded}
                    onEnded={() => {
                      completeCurrentLesson();
                    }}
                  />
                </AnimatedFadeInComponent>
              ) : null}

              {currentLesson?.highlightContent?.audio ? (
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateAudio
                    className="!my-0"
                    audio={currentLesson?.highlightContent?.audio}
                    onEnded={() => {
                      completeCurrentLesson();
                    }}
                  />
                </AnimatedFadeInComponent>
              ) : null}

              {currentLesson?.highlightContent?.image ? (
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateMedia
                    component={
                      {
                        id: 'Media',
                        media: currentLesson?.highlightContent?.image?.media,
                      } as ComponentCommonMedia
                    }
                    className="!my-0"
                  />
                </AnimatedFadeInComponent>
              ) : null}
            </div>

            <div
              className={classNames('grid grid-cols-1 gap-10', {
                'md:grid-cols-[1fr_306px]': (currentModule?.lessons || []).length > 1,
              })}>
              <AnimatedFadeInComponent>
                <div className="pb-4">
                  {cx?.documentId ? (
                    <TitleWithActions
                      title={currentLesson?.title || ''}
                      entity={cx}
                      entityId={cx?.documentId || '0'}
                      lessonId={currentLesson?.lessonStableId || ''}
                      moduleId={currentModule?.moduleStableId || ''}
                      category={
                        c?.attributes?.type === Enum_Course_Type.Training
                          ? GENERATED.Enum_Bookmark_Category.Training
                          : GENERATED.Enum_Bookmark_Category.Cpd
                      }
                      type={Enum_Like_Type.Learn}
                      favorite={false}
                    />
                  ) : null}
                  <Divider />
                  <p className="mt-4 font-cadiz text-sh-16-24">{currentLesson?.description}</p>
                  <Divider className="mb-0" />
                </div>
              </AnimatedFadeInComponent>

              {(currentModule?.lessons || []).length > 1 ? (
                <div className="border-t border-sh-border-learn md:border-l md:border-t-0 md:pl-10">
                  <ul className="!pl-0 font-cadiz text-sh-xxs">
                    {currentModule?.lessons?.map((lesson) => {
                      const isDone = !!normalizedLearnItems?.find(
                        (learnItem) =>
                          learnItem?.idModule === currentModule?.moduleStableId &&
                          learnItem?.idLesson === lesson?.lessonStableId,
                      );

                      return (
                        <ol
                          className={classNames('!pl-0', {
                            'font-bold': lesson?.lessonStableId === currentLesson?.lessonStableId,
                          })}
                          key={lesson?.lessonStableId}>
                          <AnimatedFadeInComponent>
                            <Link
                              to={`/learn/course/${c?.slug}/module/${currentModule?.moduleStableId}/lesson/${lesson?.lessonStableId}`}
                              className="mb-sh-15 flex items-center">
                              {lesson?.title}
                              <div className="ml-1 flex w-4 items-center justify-center text-primary-600">
                                <CheckIcon
                                  className={classNames('inline-block h-auto w-4 transition duration-300 ease-in', {
                                    hidden: !isDone,
                                    visible: isDone,
                                  })}
                                />
                              </div>
                            </Link>
                          </AnimatedFadeInComponent>
                        </ol>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
            {currentLesson?.objectivesSection?.id ? (
              <AnimatedFadeInComponent>
                <div className="rounded-2xl border border-sh-blue-light p-5">
                  <h4 className="mb-4 flex items-center text-sh-md font-bold">
                    {currentLesson?.objectivesSection?.title}
                  </h4>
                  <ul>
                    {currentLesson?.objectivesSection?.objectives?.map((objective, idx) => (
                      <li className="text-sh-sm" key={`objective-${idx}`}>
                        {objective?.description}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedFadeInComponent>
            ) : null}
            {nextLesson === null && !learn?.attributes?.completed && c?.type !== Enum_Course_Type.Training ? (
              <AnimatedFadeInComponent>
                <div className="flex flex-col items-center rounded-2xl border border-sh-blue-light bg-boxes-background p-5">
                  <h4 className="mb-4 flex items-center font-cadiz text-sh-28-32 font-bold">
                    {learnPage?.lastLessonSection?.title}
                  </h4>
                  <p className="mb-4 font-cadiz text-sh-16-24">{learnPage?.lastLessonSection?.description}</p>
                  <Button
                    theme="solid"
                    disabled={!!learn?.attributes?.completed}
                    onClick={() => {
                      markCourseAsCompleted();
                    }}>
                    {learn?.attributes?.completed
                      ? learnPage?.markCourseAsCompleted?.completedLabel || 'COURSE COMPLETED'
                      : learnPage?.markCourseAsCompleted?.markAsCompleteLabel || 'MARK THIS COURSE AS COMPLETE'}
                  </Button>
                </div>
              </AnimatedFadeInComponent>
            ) : null}

            {currentLesson?.textContent?.markdownText ? (
              <AnimatedFadeInComponent style={{ overflow: 'hidden' }}>
                <DefaultContentTemplateTextEditor
                  className="!my-0"
                  component={
                    {
                      id: 'textContent',
                      markdownText: currentLesson?.textContent?.markdownText || '',
                      markdownHtml: currentLesson?.textContent?.markdownText || '',
                    } as ComponentCommonTextEditorWithHtml
                  }
                />
                <Divider className="mb-0" />
              </AnimatedFadeInComponent>
            ) : null}

            {(currentLesson?.downloadableContent || []).length > 0 ? (
              <AnimatedFadeInComponent>
                <DownloadableContent
                  className="!my-0"
                  component={
                    {
                      id: 'DownloadableContent',
                      list: currentLesson?.downloadableContent,
                    } as ComponentCommonDownloadableContentList
                  }
                  iconHeader={learnPage?.contentTemplateIcons?.downloadableContentIcon?.url || undefined}
                  title={learnPage?.contentTemplateLabels?.downloadableContentLabel || undefined}
                />
              </AnimatedFadeInComponent>
            ) : null}

            {(currentLesson?.faq || []).length > 0 ? (
              <AnimatedFadeInComponent>
                <DefaultContentTemplateFaq
                  className="!my-0"
                  text={learnPage?.enhanceYourLearningSection?.title || 'Enhance your learning'}
                  component={
                    {
                      id: 'DefaultContentTemplateFaq',
                      faq: currentLesson?.faq,
                    } as ComponentCommonFaqComponent
                  }
                />
              </AnimatedFadeInComponent>
            ) : null}
          </div>

          <AnimatedFadeInComponent>
            <div className="mt-sh-30 flex flex-wrap gap-5">
              {previousIdLesson ? (
                <>
                  {renderMarkAsDoneButton()}
                  <Button
                    theme="outline"
                    as="a"
                    href={`/learn/course/${c?.slug}/module/${previousLessonidModule}/lesson/${previousIdLesson}`}>
                    <ChevronDoubleLeftIcon className="inline-block h-4 w-4" />
                    {learnPage?.previousLessonButtonLabel}
                  </Button>
                </>
              ) : (
                <>{renderMarkAsDoneButton()}</>
              )}
            </div>
          </AnimatedFadeInComponent>
          {(currentLesson?.authors as unknown as AuthorRelationResponseCollection)?.data &&
            (currentLesson?.authors as unknown as AuthorRelationResponseCollection)?.data?.length > 0 && (
              <AnimatedFadeInComponent>
                <>
                  <Divider isBody />

                  <DefaultContentTemplateAuthorDetailed
                    component={
                      {
                        id: 'DefaultContentTemplateAuthorDetailed',
                        authors: currentLesson?.authors,
                      } as ComponentCommonAuthorDetailed
                    }
                  />
                </>
              </AnimatedFadeInComponent>
            )}

          {nextLesson ? (
            <AnimatedFadeInComponent>
              <Divider isBody />
              <p className="mb-sh-30 font-cadiz text-sh-20-28 font-bold">
                {idNextLessonModule === idModule ? 'Next Lesson' : 'Next Module'}
              </p>
              <LessonCard
                onClickButton={() => {
                  completeCurrentLesson();
                }}
                title={nextLesson!.title}
                description={nextLesson!.description}
                thumbnail={nextLesson!.thumbnail}
                watchLabel={learnPage?.nextLessonButtonLabel}
                watchAgainLabel={learnPage?.nextLessonButtonWatchAgainLabel}
                tag="Lesson"
                duration={nextLesson?.duration || ''}
                done={
                  !!normalizedLearnItems?.find(
                    (learnItem) => learnItem?.idModule === idNextLessonModule && learnItem?.idLesson === nextLesson?.id,
                  )
                }
                url={`/learn/course/${c?.slug}/module/${idNextLessonModule}/lesson/${nextLesson?.lessonStableId}`}
              />
            </AnimatedFadeInComponent>
          ) : null}

          {(relatedContent || []).length > 0 ? (
            <>
              <Divider isBody />
              <div className="mt-sh-60">
                <h3 className="mb-6 text-sh-md font-bold">You might enjoy</h3>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {relatedContent!.map((course) => {
                    return <CourseCard course={course} key={course.title} />;
                  })}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </Container>
    </>
  );
}

export default CourseLessonPage;
